import { ensurePurchaseLineIds, newPurchaseLine } from '../purchaseLines';
import PermissionButton from '../../../permissions/PermissionButton';
import React, { useState, useEffect } from 'react';
import { Card, Space, Button, Tag, Spin, Alert, message, Modal, Drawer, notification, Select, Input } from 'antd';
import { CheckOutlined, CloseOutlined, SendOutlined, UndoOutlined, SaveOutlined, CloudDownloadOutlined, EditOutlined } from '@ant-design/icons';
import { motion } from 'framer-motion';
import NestedAchatTable from '../NestedAchatTable';
import AddItemToReceptionForm from './AddItemToReceptionForm';
// price updates handled via PriceUpdateModal
import PriceUpdateModal from '../PriceUpdateModal';
import DBFImportModal from '../DBFImportModal';
import ExcelDetailImport from '../../shared/ExcelDetailImport';
import {
  useFetchBonReceptionById,
  useFetchItemsInBonReception,
  useValidateBonReception,
  useUnvalidateBonReception,
  useTransferReceptionToFacture,
  useSaveBonReception,
  useListDBFFiles,
  useImportFromDBF,
} from '../../../hooks/achatReceptionHooks';
import { useFetchAllEntrepots, useDefaultEntrepot } from '../../../hooks/entrepotHooks';
import EditableDateField from '../../../utils/EditableDateField';
import { formatAmount } from '../../../utils/formatAmount';
import FourList from '../../four/FourList';

const SingleReception = ({ id, onStatusChange, onClose, onCancel, onValidate, onUnvalidate: onUnvalidateProp, userPermissions = { isManager: false } }) => {
  const { data: initialReception, loading, error, refetch } = useFetchBonReceptionById(id);
  const { items: initialItems, loading: itemsLoading, refetch: refetchItems } = useFetchItemsInBonReception(id);
  const { validate, loading: validateLoading } = useValidateBonReception();
  const { unvalidate, loading: unvalidateLoading } = useUnvalidateBonReception();
  const { transfer, loading: transferLoading } = useTransferReceptionToFacture();
  const { save } = useSaveBonReception();
  const [isClosing, setIsClosing] = useState(false);
  const [isAddItemDrawerVisible, setIsAddItemDrawerVisible] = useState(false);
  const [isDBFImportModalVisible, setIsDBFImportModalVisible] = useState(false);
  
  const [localReception, setLocalReception] = useState(null);
  const [localItems, setLocalItems] = useState([]);
  const [hasLocalChanges, setHasLocalChanges] = useState(false);
  const [isPriceModalOpen, setIsPriceModalOpen] = useState(false);
  const [fournisseurDrawerOpen, setFournisseurDrawerOpen] = useState(false);
  const { entrepots } = useFetchAllEntrepots();
  const { defaultCode } = useDefaultEntrepot();

  // Use onCancel if onClose is not provided
  const handleCloseCallback = onClose || onCancel;

  // Initialize local state when data loads
  useEffect(() => {
    if (initialReception) {
      setLocalReception({ ...initialReception });
    }
  }, [initialReception]);

  // Prefill default entrepot when reception has none (editable only)
  useEffect(() => {
    if (!localReception || !defaultCode) return;
    if (localReception.VALIDER || localReception.EN_FACTURE) return;
    if (localReception.CODE_ENT) return;

    setLocalReception(prev => (prev && !prev.CODE_ENT ? { ...prev, CODE_ENT: defaultCode } : prev));
    setHasLocalChanges(true);
  }, [
    defaultCode,
    localReception?.id,
    localReception?.CODE_ENT,
    localReception?.VALIDER,
    localReception?.EN_FACTURE,
  ]);

  useEffect(() => {
    if (initialItems) {
      setLocalItems(ensurePurchaseLineIds(initialItems));
    }
  }, [initialItems]);

  // Helper function to calculate totals from items
  const calculateTotals = (items) => {
    console.log('=== Calculating totals from items (summing TotalHT and TotalTTC) ===');
    const totals = (items || []).reduce(
      (acc, item, index) => {
        if (!item) return acc;
  
        // Use the TotalHT and TotalTTC stored in each item
        // If they don't exist, calculate them from the item's own data
        let itemTotalHT;
        let itemTotalTTC;
        
        if (item.TotalHT !== undefined && item.TotalHT !== null) {
          // Use existing TotalHT
          itemTotalHT = Number(item.TotalHT);
          console.log(`Item ${index}: ${item.CODE_ART} - Using stored TotalHT: ${itemTotalHT}`);
        } else {
          // Calculate TotalHT from item's own data
          const quantity = Number(item.QTE) || 0;
          const paHT = Number(item.PA_HT) || 0;
          itemTotalHT = quantity * paHT;
          console.log(`Item ${index}: ${item.CODE_ART} - Calculated TotalHT: qty(${quantity}) * PA_HT(${paHT}) = ${itemTotalHT}`);
        }
        
        if (item.TotalTTC !== undefined && item.TotalTTC !== null) {
          // Use existing TotalTTC
          itemTotalTTC = Number(item.TotalTTC);
        } else {
          // Calculate TotalTTC from item's own data
          const quantity = Number(item.QTE) || 0;
          const paTTC = Number(item.PA_TTC) || 0;
          itemTotalTTC = quantity * paTTC;
        }
  
        return {
          totalHT:  acc.totalHT  + itemTotalHT,
          totalTTC: acc.totalTTC + itemTotalTTC,
        };
      },
      { totalHT: 0, totalTTC: 0 }
    );
    
    console.log('✅ Final totals - HT:', totals.totalHT, 'TTC:', totals.totalTTC);
    return totals;
  };

  const handleSave = (row) => {
    setLocalItems((prevItems) => {
      const updatedRow = row;

      const updated = prevItems.map((item) =>
        item.id === row.id ? updatedRow : item
      );
      
      // Calculate new totals and update localReception
      const totals = calculateTotals(updated);
      setLocalReception(prev => ({
        ...prev,
        MNT_HT: totals.totalHT,
        MNT_TTC: totals.totalTTC,
      }));
      
      return updated;
    });
    setHasLocalChanges(true);
  };

  const handleDelete = (lineId) => {
    setLocalItems((prevItems) => {
      const updated = prevItems.filter(item => item.id !== lineId);
      
      // Calculate new totals and update localReception
      const totals = calculateTotals(updated);
      setLocalReception(prev => ({
        ...prev,
        MNT_HT: totals.totalHT,
        MNT_TTC: totals.totalTTC,
      }));
      
      return updated;
    });
    setHasLocalChanges(true);
    message.success("Article supprimé localement. Cliquez sur 'Enregistrer' pour sauvegarder.");
  };

  const handleAddItemSuccess = (payload) => {
    const itemsToAdd = Array.isArray(payload) ? payload : [payload];
    setLocalItems((prevItems) => {
      const updated = [...prevItems];
      itemsToAdd.forEach((newItem) => {
        if (!newItem || !newItem.CODE_ART) return;
        
        // Calculate TotalHT and TotalTTC for the new item if not already set
        const quantity = Number(newItem.QTE) || 0;
        const paHT = Number(newItem.PA_HT) || 0;
        const paTTC = Number(newItem.PA_TTC) || 0;
        
        const itemWithTotals = {
          ...newItem,
          TotalHT: newItem.TotalHT !== undefined ? newItem.TotalHT : (quantity * paHT),
          TotalTTC: newItem.TotalTTC !== undefined ? newItem.TotalTTC : (quantity * paTTC),
        };
        
        updated.push(newPurchaseLine(itemWithTotals));
      });
      
      // Calculate new totals and update localReception
      const totals = calculateTotals(updated);
      setLocalReception(prev => ({
        ...prev,
        MNT_HT: totals.totalHT,
        MNT_TTC: totals.totalTTC,
      }));
      
      return updated;
    });
    setHasLocalChanges(true);
    message.success(itemsToAdd.length > 1 ? `${itemsToAdd.length} articles ajoutés` : 'Article ajouté');
  };

  const handleSaveChanges = async () => {
    try {
      const cleanItems = localItems.map(({ article, ...item }) => {
        const quantity = Number(item.QTE) || 0;
        const paHT = Number(item.PA_HT) || 0;
        const paTTC = Number(item.PA_TTC) || 0;
        
        return {
          ...item,
          TotalHT: item.TotalHT !== undefined && item.TotalHT !== null 
            ? item.TotalHT 
            : (quantity * paHT),
          TotalTTC: item.TotalTTC !== undefined && item.TotalTTC !== null 
            ? item.TotalTTC 
            : (quantity * paTTC),
        };
      });

      await save(localReception, cleanItems);
      setHasLocalChanges(false);
      message.success("Bon de réception enregistré avec succès!");
      await refetch();
      await refetchItems();
    } catch (error) {
      console.error("Error saving reception:", error);
      message.error(`Échec de l'enregistrement: ${error.message || error}`);
    }
  };

  const handleFournisseurSelect = (four) => {
    if (!four?.code_frs) return;
    setLocalReception((prev) => ({
      ...prev,
      CODE_FRS: four.code_frs,
      FOURNISSEUR: four.sociale,
      fournisseur: {
        ...(prev?.fournisseur || {}),
        ...four,
      },
    }));
    setHasLocalChanges(true);
    setFournisseurDrawerOpen(false);
    message.success(`Fournisseur changé: ${four.sociale}${four.compte ? ` (${four.compte})` : ''}`);
  };

  const doValidate = async () => {
    try {
      await validate(id);
      message.success('Bon de réception validé avec succès');
      await refetch();
      await refetchItems();
      if (onValidate) onValidate();
      if (onStatusChange) onStatusChange();
    } catch (error) {
      message.error(`Échec de la validation: ${error.message || error}`);
    }
  };

  const doUnvalidate = async () => {
    Modal.confirm({
      title: 'Dévalider le bon de réception',
      content: 'Êtes-vous sûr de vouloir dévalider ce bon de réception? Cela décrémentera le stock.',
      okText: 'Oui, dévalider',
      cancelText: 'Annuler',
      onOk: async () => {
        try {
          await unvalidate(id);
          message.success('Bon de réception dévalidé avec succès');
          await refetch();
          await refetchItems();
          if (onUnvalidateProp) onUnvalidateProp();
          if (onStatusChange) onStatusChange();
        } catch (error) {
          message.error(`Échec de la dévalidation: ${error.message || error}`);
        }
      }
    });
  };

  const doTransfer = async () => {
    Modal.confirm({
      title: 'Créer une facture achat',
      content: `Voulez-vous créer une facture achat à partir du bon de réception ${localReception?.REF_BR_A}?`,
      okText: 'Oui, créer',
      cancelText: 'Annuler',
      onOk: async () => {
        try {
          const result = await transfer(id);
          message.success(`Facture créée: ${result?.REF_FAC_A || ''}`);
          await refetch();
          onStatusChange && onStatusChange();
        } catch (error) {
          message.error(`Échec du transfert: ${error.message || error}`);
        }
      }
    });
  };

  const openPriceModal = () => setIsPriceModalOpen(true);
  const closePriceModal = () => setIsPriceModalOpen(false);

  const handleDBFImport = (records) => {
    if (!records || records.length === 0) {
      message.warning('Aucun enregistrement à importer');
      return;
    }

    // Each imported row remains an independent purchase line.
    const addedItems = records.filter(record => record?.CODE_ART).map(record => newPurchaseLine({
      ...record,
      REF_BR_A: localReception.REF_BR_A, qte_recue: -1, controle: false,
    }));
    const updatedItems = [...addedItems, ...localItems];
    const addedCount = addedItems.length;

    setLocalItems(updatedItems);
    
    // Calculate new totals and update localReception
    const totals = calculateTotals(updatedItems);
    setLocalReception(prev => ({
      ...prev,
      MNT_HT: totals.totalHT,
      MNT_TTC: totals.totalTTC,
    }));
    
    setHasLocalChanges(true);
    message.success(`${addedCount} ligne(s) ajoutée(s)`);
  };

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      handleCloseCallback && handleCloseCallback();
    }, 120);
  };

  if (loading) return <Spin tip="Chargement..." style={{ display: 'block', margin: '50px auto' }} />;
  if (error) return <Alert message={`Erreur: ${error}`} type="error" showIcon />;
  if (!localReception || !localItems) return <Spin tip="Chargement..." style={{ display: 'block', margin: '50px auto' }} />;

  // Calculate taxe for display
  const taxe = (localReception?.MNT_TTC || 0) - (localReception?.MNT_HT || 0);
  
  // Render the header info section
  const renderHeaderInfo = () => (
    <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap', alignItems: 'flex-start' }}>
      {/* Left section - Main info */}
      <div style={{ flex: '1 1 400px', minWidth: '300px' }}>
        {/* First row - Fournisseur, Ref BL, Ref Commande, Date */}
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: '12px', flexWrap: 'wrap', gap: '16px' }}>
          <div>
            <div style={{ fontSize: '13px', color: '#999', textTransform: 'uppercase', marginBottom: '4px' }}>Fournisseur</div>
            <div style={{ fontSize: '16px', fontWeight: 500, display: 'flex', alignItems: 'center', gap: 4 }}>
              <span>
                {localReception.FOURNISSEUR}
                {localReception.fournisseur?.compte && (
                  <span style={{ color: '#888', fontWeight: 400, marginLeft: 8 }}>
                    (Compte: {localReception.fournisseur.compte})
                  </span>
                )}
              </span>
              {!localReception.VALIDER && (
                <PermissionButton permissionModule="purchaseReceipts" permissionAction="update"
                  type="link"
                  icon={<EditOutlined />}
                  size="small"
                  style={{ padding: '0 4px' }}
                  onClick={() => setFournisseurDrawerOpen(true)}
                />
              )}
            </div>
          </div>
          {/* Ref BL */}
          <div>
            <div style={{ fontSize: '13px', color: '#999', textTransform: 'uppercase', marginBottom: '4px' }}>Réf. BL Fournisseur</div>
            {!localReception.VALIDER && !localReception.EN_FACTURE ? (
              <Input
                size="small"
                style={{ width: '140px', fontSize: '15px' }}
                placeholder="Réf. BL Frs"
                value={localReception.REF_BL || ''}
                onChange={(e) => {
                  setLocalReception(prev => ({ ...prev, REF_BL: e.target.value }));
                  setHasLocalChanges(true);
                }}
              />
            ) : (
              <div style={{ fontSize: '16px', fontWeight: 500, color: '#333' }}>{localReception.REF_BL || '—'}</div>
            )}
          </div>
          {/* Ref Commande */}
          <div>
            <div style={{ fontSize: '13px', color: '#999', textTransform: 'uppercase', marginBottom: '4px' }}>Ref. Commande</div>
            <div style={{ fontSize: '16px', fontWeight: 500 }}>{localReception.REF_CA || '—'}</div>
          </div>
          {/* Date */}
          <div>
            <div style={{ fontSize: '13px', color: '#999', textTransform: 'uppercase', marginBottom: '4px' }}>Date Réception</div>
            <div style={{ fontSize: '16px', fontWeight: 500 }}>
              <EditableDateField
                value={localReception.DATE_BR}
                onChange={(newDate) => {
                  setLocalReception(prev => ({ ...prev, DATE_BR: newDate }));
                  setHasLocalChanges(true);
                }}
                disabled={!!localReception.VALIDER || !!localReception.EN_FACTURE}
              />
            </div>
          </div>
        </div>
        
        {/* Second row - Remarque */}
        <div>
          <div style={{ fontSize: '13px', color: '#999', textTransform: 'uppercase', marginBottom: '4px' }}>Remarque</div>
          {!localReception.VALIDER && !localReception.EN_FACTURE ? (
            <Input
              size="small"
              style={{ width: '100%', fontSize: '15px' }}
              placeholder="Remarque..."
              value={localReception.REMARQUE || ''}
              onChange={(e) => {
                setLocalReception(prev => ({ ...prev, REMARQUE: e.target.value }));
                setHasLocalChanges(true);
              }}
            />
          ) : (
            <div style={{ fontSize: '16px', color: '#555', fontWeight: 500 }}>{localReception.REMARQUE || '—'}</div>
          )}
        </div>
      </div>
      
      {/* Right section - Totals (bigger) */}
      <div style={{ 
        flex: '0 0 auto',
        minWidth: '180px',
        padding: '16px 20px',
        background: '#fafafa',
        borderRadius: '8px',
        border: '1px solid #f0f0f0'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
          <span style={{ fontSize: '14px', color: '#555' }}>Total HT</span>
          <span style={{ fontSize: '15px', fontWeight: 500, color: '#333' }}>
            {formatAmount(localReception.MNT_HT)}
          </span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
          <span style={{ fontSize: '14px', color: '#555' }}>TVA</span>
          <span style={{ fontSize: '15px', fontWeight: 500, color: '#333' }}>
            {formatAmount(taxe)}
          </span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '10px', borderTop: '1px solid #e0e0e0' }}>
          <span style={{ fontSize: '14px', color: '#555' }}>Total TTC</span>
          <span style={{ fontSize: '15px', fontWeight: 500, color: '#333' }}>
            {formatAmount(localReception.MNT_TTC)}
          </span>
        </div>
      </div>
    </div>
  );

  // Animation variants
  const containerVariants = {
    hidden: {
      opacity: 0,
      scale: 0.95,
      transition: {
        duration: 0.15,
        ease: [0.25, 0.1, 0.25, 1.0]
      }
    },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.15,
        ease: [0.25, 0.1, 0.25, 1.0]
      }
    },
    exit: {
      opacity: 0,
      scale: 0.97,
      transition: {
        duration: 0.12,
        ease: [0.25, 0.1, 0.25, 1.0]
      }
    }
  };

  return (
    <motion.div
      initial="hidden"
      animate={isClosing ? "exit" : "visible"}
      variants={containerVariants}
      style={{
        transformOrigin: 'center center',
        willChange: 'transform, opacity'
      }}
    >
      <Card
        title={
          <Space>
            <span style={{ fontSize: '14px' }}>
            {localReception.REF_BL || 'BR'} <span style={{ color: '#888', fontWeight: 400, fontSize: '13px' }}>#{localReception.REF_BR_A}</span>
            </span>
            {localReception.EN_FACTURE ? (
              <Tag color="blue">Transféré</Tag>
            ) : localReception.VALIDER ? (
              <Tag color="green">Validé</Tag>
            ) : (
              <Tag color="orange">Enregistré</Tag>
            )}
            {!localReception.VALIDER && !localReception.EN_FACTURE ? (
              <Select
                size="small"
                style={{ minWidth: 200 }}
                placeholder="Entrepôt"
                value={localReception?.CODE_ENT}
                onChange={(value) => {
                  setLocalReception(prev => ({ ...prev, CODE_ENT: value }));
                  setHasLocalChanges(true);
                }}
                options={(entrepots || []).map(e => ({ value: e.code_ent, label: `${e.code_ent} - ${e.name}` }))}
              />
            ) : localReception?.CODE_ENT ? (
              <Tag color="blue">
                Entrepôt: {localReception.entrepot ? `${localReception.entrepot.code_ent} - ${localReception.entrepot.name}` : localReception.CODE_ENT}
              </Tag>
            ) : null}
          </Space>
        }
        bordered={false}
        extra={
          <Space>
            {hasLocalChanges && (
              <PermissionButton
                icon={<UndoOutlined />}
                onClick={() => {
                  Modal.confirm({
                    title: 'Annuler les modifications',
                    content: 'Êtes-vous sûr de vouloir annuler toutes les modifications?',
                    okText: 'Oui, Annuler tout',
                    cancelText: 'Non, Garder les modifications',
                    onOk: () => {
                      setLocalReception({ ...initialReception });
                      setLocalItems(ensurePurchaseLineIds(initialItems));
                      setHasLocalChanges(false);
                      message.success('Toutes les modifications ont été annulées');
                    }
                  });
                }}
              >
                Annuler modif.
              </PermissionButton>
            )}
            {!localReception.VALIDER && !localReception.EN_FACTURE && (
              <>
                <PermissionButton permissionModule="purchaseReceipts" permissionAction="update"
                  type="primary"
                  icon={<SaveOutlined />}
                  onClick={handleSaveChanges}
                  disabled={!hasLocalChanges}
                >
                  Enregistrer Modif.
                </PermissionButton>
                <PermissionButton permissionModule="purchaseReceipts" permissionAction="validate"
                  type="primary"
                  icon={<CheckOutlined />}
                  onClick={doValidate}
                  loading={validateLoading}
                  disabled={hasLocalChanges}
                >
                  Valider
                </PermissionButton>
              </>
            )}
            {localReception.VALIDER && !localReception.EN_FACTURE && (
              <>
                <PermissionButton permissionModule="purchaseReceipts" permissionAction="unvalidate"
                  danger
                  icon={<CloseOutlined />}
                  onClick={doUnvalidate}
                  loading={unvalidateLoading}
                >
                  Dévalider
                </PermissionButton>
                <PermissionButton permissionModule="purchaseReceipts" permissionAction="transfer"
                  type="primary"
                  icon={<SendOutlined />}
                  onClick={doTransfer}
                  loading={transferLoading}
                >
                  Transférer {'>'} Facture
                </PermissionButton>
              </>
            )}
            <Button
              type="default"
              onClick={handleClose}
              style={{
                backgroundColor: 'white',
                color: '#EE2625',
                border: '1px solid #EE2625',
              }}
            >
              SORTIR
            </Button>
          </Space>
        }
        style={{ position: 'relative' }}
      >
        {renderHeaderInfo()}
      </Card>

      <Card
        title={`Articles (${localItems?.length || 0})`}
        style={{ marginTop: 20 }}
        loading={itemsLoading}
        extra={
          <Space>
            <Button onClick={openPriceModal} type="default">Mettre à jour les prix</Button>
            {!localReception.VALIDER && !localReception.EN_FACTURE && (
              <>
                <ExcelDetailImport
                  type="reception"
                  onImport={handleDBFImport}
                />
                <Button
                  icon={<CloudDownloadOutlined />}
                  onClick={() => setIsDBFImportModalVisible(true)}
                  type="default"
                >
                  Importer FTP
                </Button>
                <PermissionButton permissionModule="purchaseReceipts" permissionAction="update"
                  onClick={() => setIsAddItemDrawerVisible(true)}
                  type="primary"
                >
                  Ajouter Article
                </PermissionButton>
              </>
            )}
          </Space>
        }
      >
        <NestedAchatTable
          items={localItems || []}
          handleSave={handleSave}
          handleDelete={handleDelete}
          validated={localReception.VALIDER || localReception.EN_FACTURE}
          userPermissions={userPermissions}
          type="reception"
          codeEnt={localReception?.CODE_ENT}
          showStock={true}
        />
      </Card>

      <Drawer
        title="Ajouter un article à la réception"
        width={900}
        onClose={() => setIsAddItemDrawerVisible(false)}
        open={isAddItemDrawerVisible}
      >
        <AddItemToReceptionForm
          refReception={localReception.REF_BR_A}
          onSuccess={(newItem) => {
            handleAddItemSuccess(newItem);
            setIsAddItemDrawerVisible(false);
          }}
        />
      </Drawer>

      <Drawer
        title="Changer le fournisseur"
        width={900}
        onClose={() => setFournisseurDrawerOpen(false)}
        open={fournisseurDrawerOpen}
      >
        <FourList onSelectFour={handleFournisseurSelect} />
      </Drawer>

      <PriceUpdateModal
        open={isPriceModalOpen}
        onClose={closePriceModal}
        items={localItems}
        onApplied={() => setIsPriceModalOpen(false)}
        title="Mise à jour des prix (Réception)"
        compareWithArticle
      />

      <DBFImportModal
        visible={isDBFImportModalVisible}
        onClose={() => setIsDBFImportModalVisible(false)}
        onImport={handleDBFImport}
        useListHook={useListDBFFiles}
        useImportHook={useImportFromDBF}
      />
    </motion.div>
  );
};

export default SingleReception;
